import React, { useState, useEffect } from 'react';
import { DollarSign, Plus, RefreshCw, Edit2, Trash2 } from 'lucide-react';
import { dealerService } from '../services/dealer.service';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { formatMoney, currencySymbol } from '../utils/formatMoney';

const emptyForm = {
  client_scheme_id: '',
  service_type_code: '',
  service_subtype_code: '',
  time_slot: 'DAY',
  time_slot_start: '06:00',
  time_slot_end: '18:00',
  base_service_rate: '',
  base_max_kms: '',
  additional_rate_per_km: '',
  fixed_rate: '',
  cancel_charges_per_km_upto_base_kms: '',
  cancel_charges_per_km_above_base_kms: '',
  effective_from: '',
  effective_to: '',
  is_active: true,
};

function RateModal({ isOpen, onClose, onSave, rate, schemes }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [serviceTypes, setServiceTypes] = useState([]);
  const [serviceSubtypes, setServiceSubtypes] = useState([]);

  // Load service types for the selected scheme (cascading dropdown source).
  useEffect(() => {
    if (!form.client_scheme_id) { setServiceTypes([]); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const data = await dealerService.getSchemeServiceTypes({ scheme_id: form.client_scheme_id, is_active: true });
        if (!cancelled) setServiceTypes(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setServiceTypes([]);
      }
    })();
    return () => { cancelled = true; };
  }, [form.client_scheme_id]);

  const selectedType = serviceTypes.find(t => t.service_type_code === form.service_type_code);

  // Load subtypes for the selected service type.
  useEffect(() => {
    if (!selectedType?.id) { setServiceSubtypes([]); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const data = await dealerService.getSchemeServiceSubtypes({ service_type_id: selectedType.id, is_active: true });
        if (!cancelled) setServiceSubtypes(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setServiceSubtypes([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedType?.id]);

  useEffect(() => {
    if (rate) {
      setForm({
        client_scheme_id: rate.client_scheme_id || '',
        service_type_code: rate.service_type_code || '',
        service_subtype_code: rate.service_subtype_code || '',
        time_slot: rate.time_slot || 'DAY',
        time_slot_start: rate.time_slot_start?.slice(0, 5) || '06:00',
        time_slot_end: rate.time_slot_end?.slice(0, 5) || '18:00',
        base_service_rate: rate.base_service_rate ?? '',
        base_max_kms: rate.base_max_kms ?? '',
        additional_rate_per_km: rate.additional_rate_per_km ?? '',
        fixed_rate: rate.fixed_rate ?? '',
        cancel_charges_per_km_upto_base_kms: rate.cancel_charges_per_km_upto_base_kms ?? '',
        cancel_charges_per_km_above_base_kms: rate.cancel_charges_per_km_above_base_kms ?? '',
        effective_from: rate.effective_from || '',
        effective_to: rate.effective_to || '',
        is_active: rate.is_active !== false,
      });
    } else {
      setForm(emptyForm);
    }
    setError('');
  }, [rate, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => {
      const next = { ...f, [name]: type === 'checkbox' ? checked : value };
      // Cascading resets so stale codes can't be submitted.
      if (name === 'client_scheme_id') {
        next.service_type_code = '';
        next.service_subtype_code = '';
      } else if (name === 'service_type_code') {
        next.service_subtype_code = '';
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        client_scheme_id: parseInt(form.client_scheme_id),
        base_service_rate: parseFloat(form.base_service_rate),
        base_max_kms: parseFloat(form.base_max_kms),
        additional_rate_per_km: parseFloat(form.additional_rate_per_km),
        fixed_rate: form.fixed_rate !== '' ? parseFloat(form.fixed_rate) : null,
        cancel_charges_per_km_upto_base_kms: parseFloat(form.cancel_charges_per_km_upto_base_kms),
        cancel_charges_per_km_above_base_kms: parseFloat(form.cancel_charges_per_km_above_base_kms),
        service_subtype_code: form.service_subtype_code || null,
        effective_to: form.effective_to || null,
      };
      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save rate.');
    } finally {
      setSaving(false);
    }
  };

  const numField = (name, label, required = false) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}{required ? ' *' : ''}</label>
      <input name={name} type="number" step="0.01" value={form[name]} onChange={handleChange} required={required}
        className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-screen overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">{rate ? 'Edit Rate' : 'New Rate'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Scheme *</label>
              <select name="client_scheme_id" value={form.client_scheme_id} onChange={handleChange} required
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
                <option value="">Select scheme</option>
                {schemes.map(s => <option key={s.id} value={s.id}>{s.scheme_name || s.name || s.id}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service Type *</label>
              <select name="service_type_code" value={form.service_type_code} onChange={handleChange} required
                disabled={!form.client_scheme_id}
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100">
                <option value="">{form.client_scheme_id ? 'Select type' : 'Select scheme first'}</option>
                {serviceTypes.map(t => (
                  <option key={t.id} value={t.service_type_code}>{t.service_type_name} ({t.service_type_code})</option>
                ))}
                {form.service_type_code && !selectedType && (
                  <option value={form.service_type_code}>{form.service_type_code}</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtype</label>
              <select name="service_subtype_code" value={form.service_subtype_code} onChange={handleChange}
                disabled={!form.service_type_code}
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100">
                <option value="">{form.service_type_code ? 'None' : 'Select type first'}</option>
                {serviceSubtypes.map(s => (
                  <option key={s.id} value={s.subtype_code}>{s.subtype_name} ({s.subtype_code})</option>
                ))}
                {form.service_subtype_code && !serviceSubtypes.some(s => s.subtype_code === form.service_subtype_code) && (
                  <option value={form.service_subtype_code}>{form.service_subtype_code}</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time Slot *</label>
              <select name="time_slot" value={form.time_slot} onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
                {['DAY', 'NIGHT', 'PEAK', 'OFF_PEAK'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slot Start</label>
              <input type="time" name="time_slot_start" value={form.time_slot_start} onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slot End</label>
              <input type="time" name="time_slot_end" value={form.time_slot_end} onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            {numField('base_service_rate', `Base Service Rate (${currencySymbol()})`, true)}
            {numField('base_max_kms', 'Base Max KMs', true)}
            {numField('additional_rate_per_km', `Additional Rate/KM (${currencySymbol()})`, true)}
            {numField('fixed_rate', `Fixed Rate (${currencySymbol()})`)}
            {numField('cancel_charges_per_km_upto_base_kms', 'Cancel Charges/KM (upto base)', true)}
            {numField('cancel_charges_per_km_above_base_kms', 'Cancel Charges/KM (above base)', true)}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Effective From *</label>
              <input type="date" name="effective_from" value={form.effective_from} onChange={handleChange} required
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Effective To</label>
              <input type="date" name="effective_to" value={form.effective_to} onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" name="is_active" id="rate_active" checked={form.is_active} onChange={handleChange} />
            <label htmlFor="rate_active" className="text-sm font-medium text-gray-700">Active</label>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ClientServiceRates = () => {
  const [rates, setRates] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterScheme, setFilterScheme] = useState('');
  const [filterCode, setFilterCode] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 50;

  useEffect(() => {
    loadSchemes();
  }, []);

  useEffect(() => {
    fetchRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterScheme]);

  const loadSchemes = async () => {
    try {
      const data = await dealerService.getClientSchemes();
      setSchemes(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      console.error('Error loading schemes:', err);
    }
  };

  const fetchRates = async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (filterScheme) params.scheme_id = filterScheme;
      if (filterCode) params.service_type_code = filterCode;
      const data = await dealerService.getClientServiceRates(params);
      setRates(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Error fetching rates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (payload) => {
    if (editingRate) {
      await dealerService.updateClientServiceRate(editingRate.id, payload);
    } else {
      await dealerService.createClientServiceRate(payload);
    }
    fetchRates();
  };

  const confirmDelete = async () => {
    try {
      await dealerService.deleteClientServiceRate(deleteId);
      setDeleteId(null);
      fetchRates();
    } catch (err) {
      console.error('Error deleting rate:', err);
    }
  };

  const schemeName = (id) => {
    const s = schemes.find(s => s.id === id);
    return s?.scheme_name || s?.name || id;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white flex justify-between items-center rounded-lg bxshadowhd mrgall py-3 padlr15 border">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <DollarSign className="w-6 h-6" /> Client Service Rates
        </h1>
        <div className="flex gap-2">
          <button onClick={fetchRates}
            className="flex items-center gap-1 px-3 py-2 border rounded-md text-sm hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => { setEditingRate(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">
            <Plus className="w-4 h-4" /> Add Rate
          </button>
        </div>
      </div>

      <div className="mrgall flex gap-3">
        <select value={filterScheme} onChange={e => { setFilterScheme(e.target.value); setPage(1); }}
          className="border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
          <option value="">All Schemes</option>
          {schemes.map(s => <option key={s.id} value={s.id}>{s.scheme_name || s.name || s.id}</option>)}
        </select>
        <input value={filterCode} onChange={e => setFilterCode(e.target.value)}
          placeholder="Service type code…"
          onKeyDown={e => e.key === 'Enter' && fetchRates()}
          className="border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
        <button onClick={() => { setPage(1); fetchRates(); }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">Filter</button>
      </div>

      <div className="bg-white rounded-lg bxshadowhd p-2 mrgall border">
        {loading ? <LoadingSpinner /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Scheme', 'Type Code', 'Subtype', 'Slot', 'Base Rate', 'Max KMs', 'Rate/KM', 'Effective', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-3 py-3 text-left font-medium text-gray-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rates.length === 0 ? (
                  <tr><td colSpan="10" className="px-4 py-6 text-center text-gray-500">No rates found</td></tr>
                ) : rates.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 text-gray-600">{schemeName(r.client_scheme_id)}</td>
                    <td className="px-3 py-3 font-mono text-gray-800">{r.service_type_code}</td>
                    <td className="px-3 py-3 text-gray-500">{r.service_subtype_code || '—'}</td>
                    <td className="px-3 py-3 text-gray-600">{r.time_slot}</td>
                    <td className="px-3 py-3 text-gray-800">{formatMoney(r.base_service_rate)}</td>
                    <td className="px-3 py-3 text-gray-600">{r.base_max_kms}</td>
                    <td className="px-3 py-3 text-gray-600">{formatMoney(r.additional_rate_per_km)}</td>
                    <td className="px-3 py-3 text-gray-500 text-xs">
                      <div>{r.effective_from}</div>
                      {r.effective_to && <div className="text-gray-400">→ {r.effective_to}</div>}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`px-2 py-1 text-xs rounded-md ${r.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {r.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => { setEditingRate(r); setShowModal(true); }} className="text-blue-600 hover:text-blue-800">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteId(r.id)} className="text-red-600 hover:text-red-800">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {total > LIMIT && (
              <div className="flex justify-between items-center px-4 py-3 border-t">
                <span className="text-sm text-gray-600">{total} total</span>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-40">Prev</button>
                  <span className="px-3 py-1 text-sm">Page {page}</span>
                  <button onClick={() => setPage(p => p + 1)} disabled={page * LIMIT >= total}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <RateModal isOpen={showModal} onClose={() => setShowModal(false)} onSave={handleSave}
        rate={editingRate} schemes={schemes} />
      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={confirmDelete}
        title="Delete Rate" message="Delete this service rate?" />
    </div>
  );
};

export default ClientServiceRates;
